import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import type { CalculateOrderResponse } from '../models/calculation.types';

interface PromoCodeFieldProps {
  value: string;
  isLoading: boolean;
  error: string | null;
  isValid: boolean | null;
  response: CalculateOrderResponse | null;
  onPromoCodeChange: (value: string) => void;
  onApplyPromoCode: () => void;
}

const formatPromocodeAmount = (amount: number | undefined) => {
  if (amount === undefined || !Number.isFinite(amount)) {
    return null;
  }

  return `${(amount / 100).toLocaleString('ru-RU', {
    maximumFractionDigits: 2,
  })} ₽`;
};

/**
 * Компонент поля ввода промокода
 *
 * Отображает:
 * - Поле ввода промокода
 * - Кнопку применения
 * - Сообщения об ошибках/успехе валидации
 *
 * @param value - введённый промокод
 * @param isLoading - флаг загрузки проверки промокода
 * @param error - текст ошибки валидации
 * @param isValid - результат валидации промокода
 * @param onPromoCodeChange - коллбек изменения значения промокода
 * @param onApplyPromoCode - коллбек применения промокода
 */
export const PromoCodeField = ({
  value,
  isLoading,
  error,
  isValid,
  response,
  onPromoCodeChange,
  onApplyPromoCode,
}: PromoCodeFieldProps) => {
  const discount = response?.discount;
  const amount = discount?.amount;
  const percent = discount?.percent;
  const discountLabel =
    amount != null ? formatPromocodeAmount(amount) : percent != null ? `${percent}%` : null;
  const successMessage = [
    response?.statusDescription || 'Промокод применен',
    discountLabel ? `скидка ${discountLabel}` : null,
  ]
    .filter(Boolean)
    .join(': ');

  return (
    <div>
      <label htmlFor="promoCode" className="block text-sm font-medium mb-1">
        Промокод
      </label>
      <div className="flex">
        <Input
          id="promoCode"
          name="promoCode"
          value={value}
          onChange={event => onPromoCodeChange(event.target.value)}
          disabled={isLoading}
        />
        <Button
          type="button"
          className="ml-2 cursor-pointer bg-action-primary text-action-primary-foreground hover:bg-action-primary-hover"
          disabled={isLoading || !value}
          onClick={onApplyPromoCode}
        >
          {isLoading ? 'Проверка...' : 'Применить'}
        </Button>
      </div>
      {error && <p className="text-status-error text-xs mt-1">{error}</p>}
      {isValid && !error && <p className="text-status-success text-xs mt-1">{successMessage}</p>}
      {isValid === false && !error && (
        <p className="text-status-error text-xs mt-1">Неверный промокод</p>
      )}
    </div>
  );
};
